import { Col, Row } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import { DefaultModal } from "components/Shared/DefaultModal";
import { RosterAndStaffTable } from "components/Shared/RosterAndStaffTable";
import { SeasonTable } from "components/Shared/SeasonTable";
import { PeopleTypes } from "enums/peopleTypes";
import { useFetchSeasonsByEntityId } from "hooks/useSeasonsApi";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useCopyPeople } from "../hooks/useCopyPeople";
import { useCopyPeopleSeasonsColumns } from "../hooks/useCopyPeopleSeasonsColumns";
import { usePreviewPeople } from "../hooks/usePreviewPeople";

type Props = {
  type: PeopleTypes;
  disabled: boolean;
  entityId: string;
  currentSeasonId: string;
};

export const CopyPeople = ({ type, disabled, entityId, currentSeasonId }: Props) => {
  const { formatMessage } = useIntl();
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [previewSeasonId, setPreviewSeasonId] = useState<string>();
  const [copySeasonId, setCopySeasonId] = useState<string>();
  const [openModal, setOpenModal] = useState(false);

  const { data: seasonsResponse, isLoading } = useFetchSeasonsByEntityId({
    entityId,
    enabled: openModal,
  });
  const seasons = (seasonsResponse?.data || []).filter((x: any) => x.seasonId !== currentSeasonId);

  const {
    records: previewRecords,
    columns: previewColumns,
    isLoading: isPreviewLoading,
  } = usePreviewPeople({
    type,
    entityId,
    seasonId: previewSeasonId || "undefined",
    enabled: !!previewSeasonId && openPreviewModal,
  });

  const { mutateAsync: copy, isCopying } = useCopyPeople({
    type,
    entityId,
    seasonId: currentSeasonId,
    fromSeasonId: copySeasonId || "undefined",
  });

  const seasonColumns = useCopyPeopleSeasonsColumns({
    type,
    onCopy: (record: any) => {
      setCopySeasonId(record.seasonId);
      setOpenConfirmModal(true);
      setOpenModal(false);
    },
    onPreview: (record: any) => {
      setPreviewSeasonId(record.seasonId);
      setOpenPreviewModal(true);
      setOpenModal(false);
    },
  });

  const onPreviewModalClosed = () => {
    setOpenPreviewModal(false);
    setPreviewSeasonId(undefined);
    setOpenModal(true);
  };

  const onConfirmModalClosed = () => {
    if (isCopying) {
      return;
    }
    setOpenConfirmModal(false);
    setCopySeasonId(undefined);
    setOpenModal(true);
  };

  const onCopy = async () => {
    if (!copySeasonId) {
      return;
    }
    try {
      await copy();
    } finally {
      setOpenConfirmModal(false);
      setCopySeasonId(undefined);
      setOpenModal(false);
    }
  };

  return (
    <>
      <DefaultButton
        onClick={() => setOpenModal(true)}
        disabled={disabled}
        icon={<i className="fa-solid fa-file-arrow-down" />}
      >
        {formatMessage({
          id:
            type === PeopleTypes.Roster
              ? "button.copyRoster"
              : type === PeopleTypes.Staff
                ? "button.copyStaff"
                : "notSupported",
        })}
      </DefaultButton>
      {openModal && (
        <DefaultModal open={openModal} footer={null} onCancel={() => setOpenModal(false)}>
          <Row className="mt-25">
            <Col span={24}>
              <SeasonTable seasons={seasons} isLoading={isLoading} columns={seasonColumns} />
            </Col>
          </Row>
        </DefaultModal>
      )}
      {openConfirmModal && (
        <DefaultModal
          title={formatMessage({
            id: "copyPeopleModal.confirmationModal.title",
          })}
          open={openConfirmModal}
          width={undefined}
          onOk={onCopy}
          okText={formatMessage({ id: "button.copy" })}
          okButtonProps={{ loading: isCopying }}
          cancelButtonProps={{ disabled: isCopying }}
          onCancel={onConfirmModalClosed}
        >
          {formatMessage(
            {
              id:
                type === PeopleTypes.Roster
                  ? "copyPeopleModal.confirmationModal.content.roster"
                  : type === PeopleTypes.Staff
                    ? "copyPeopleModal.confirmationModal.content.staff"
                    : "notSupported",
            },
            { objectType: formatMessage({ id: "objectType.season" }) }
          )}
        </DefaultModal>
      )}
      {openPreviewModal && (
        <DefaultModal open={openPreviewModal} footer={null} onCancel={onPreviewModalClosed}>
          <Row className="mt-25">
            <Col span={24}>
              <RosterAndStaffTable isLoading={isPreviewLoading} records={previewRecords} columns={previewColumns} />
            </Col>
          </Row>
        </DefaultModal>
      )}
    </>
  );
};
